import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Self,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectorRef
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {DEFAULT_IMAGE_CROPPER} from '@utils/constants';
import {
  PdfToolbarService
} from "@components/shared/document-viewer/pdf-toolbar/pdf-toolbar.service";
import {Subscription} from "rxjs";
import {finalize} from "rxjs/operators";
import {DocumentFileService} from "@services/document-file.service";
import {DocumentTextHighlight} from "@components/shared/document-viewer/models/document-text-highlight";
import {PdfSearchComponent} from "@components/shared/document-viewer/pdf-toolbar/pdf-search/pdf-search.component";

const STANDARD_PAGE_HEIGHT_WIDTH_RATIO = 11 / 8.5;
const DEFAULT_IMG_PLACEHOLDER_HEIGHT = 1000;
const EMPTY_HIGHLIGHTS: DocumentTextHighlight[] = [];
const CLOSE_BUTTON_TOOLTIP =
  "The document can be closed at any time. To reopen it, use the 'Show Document' toggle in the top toolbar.";

interface SearchMatch {
  ulc: { x: number, y: number };
  urc: { x: number, y: number };
  llc: { x: number, y: number };
  lrc: { x: number, y: number };
  rawText: string;
  fontSize: number;
  pageSize: { width: number, height: number };
  pageNumber: number;
}

@Component({
    selector: 'app-document-view-split-screen',
    templateUrl: './document-view-split-screen.component.html',
    styleUrls: ['./document-view-split-screen.component.scss'],
    providers: [PdfToolbarService],
    standalone: false
})
export class DocumentViewSplitScreenComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() viewSourcePage = null;
  @Input() viewSourceBox = null;

  @Input() pageImgUrls: string[];
  @Input() documentFileId: any;
  @Input() originalFileUrl: string;
  @Input() documentFileType: string;

  @Input() documentPages?: any[]  // only for csv/excel docs?

  @Output() closeSplitscreen: EventEmitter<void> = new EventEmitter();
  @Output() rerenderEvent: EventEmitter<void> = new EventEmitter();

  @ViewChildren("pageImgListItem") private pageImgListItems: QueryList<ElementRef>;
  @ViewChild("docContainer") private scrollableDocContainer: ElementRef;
  @ViewChild(PdfSearchComponent) pdfSearchComponent?: PdfSearchComponent;

  readonly closeButtonTooltip = CLOSE_BUTTON_TOOLTIP;

  errorGettingPageImg = false;
  showTable = false;
  /** Structured JSON originals (e.g. financial-statement submission): prettified source, no page images. */
  showJson = false;
  jsonFormatted = '';
  jsonLoading = false;

  pageCount: number;
  cropper = DEFAULT_IMAGE_CROPPER;
  initialized = false;
  imgLoadSub: Subscription;
  showLoadingOverlay = false;

  imgPlaceholderHeight: number = DEFAULT_IMG_PLACEHOLDER_HEIGHT;

  searchText = '';
  searchLoading = false;
  searchError = '';
  searchPerformed = false;
  searchHighlights: DocumentTextHighlight[] = [];
  activeHighlightIndex = -1;
  activeHighlightId: string = null;
  activeHighlightPageIndex = -1;
  searchSub: Subscription;
  private jsonFetchSub: Subscription;

  private highlightsPerPage: Map<number, DocumentTextHighlight[]> = new Map();

  get totalMatches(): number {
    return this.searchHighlights?.length || 0;
  }

  get currentMatchNumber(): number {
    return this.activeHighlightIndex >= 0 ? this.activeHighlightIndex + 1 : 0;
  }

  private resetSearchState(keepText: boolean) {
    if (!keepText) {
      this.searchText = '';
    }
    this.searchPerformed = false;
    this.searchHighlights = [];
    this.activeHighlightIndex = -1;
    this.activeHighlightId = null;
    this.activeHighlightPageIndex = -1;
    this.highlightsPerPage = new Map();
    this.searchError = '';
  }

  constructor(
    private _pdfToolbarService: PdfToolbarService,
    private _documentFileService: DocumentFileService,
    private _http: HttpClient,
    @Self() private element: ElementRef,
    private _cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.configureDocumentView();
  }

  /**
   * XLSX/CSV: render cells as HTML tables. JSON: fetch original file and pretty-print. Otherwise: page images / PDF raster.
   */
  private configureDocumentView() {
    this.showJson = false;
    this.showTable = false;
    this.jsonFormatted = '';

    if (this.documentFileType === 'XLSX' || this.documentFileType === 'CSV') {
      this.showTable = true;
      this.pageCount = this.documentPages?.length;
      this.errorGettingPageImg = false;
      return;
    }

    if (this.documentFileType === 'JSON') {
      this.showJson = true;
      this.pageCount = 1;
      this.errorGettingPageImg = false;
      this.loadJsonPreview();
      return;
    }

    if (!this?.pageImgUrls?.length) {
      this.errorGettingPageImg = true;
    } else {
      this.errorGettingPageImg = false;
      this.pageCount = this.pageImgUrls?.length;
      this.setImagePlaceholderDimensions();
      if (this.viewSourcePage) {
        this.resetCropper();
      }
    }
  }

  private loadJsonPreview() {
    this.jsonFetchSub?.unsubscribe();
    if (!this.originalFileUrl) {
      this.errorGettingPageImg = true;
      this._cdr.detectChanges();
      return;
    }
    this.jsonLoading = true;
    this.errorGettingPageImg = false;
    this._cdr.detectChanges();

    this.jsonFetchSub = this._http.get(this.originalFileUrl, { responseType: 'text' }).pipe(
      finalize(() => {
        this.jsonLoading = false;
        this._cdr.detectChanges();
      })
    ).subscribe({
      next: (text) => {
        try {
          const parsed = JSON.parse(text);
          this.jsonFormatted = JSON.stringify(parsed, null, 2);
        } catch {
          this.jsonFormatted = text;
        }
        this.errorGettingPageImg = false;
      },
      error: () => {
        this.jsonFormatted = '';
        this.errorGettingPageImg = true;
      },
    });
  }

  ngAfterViewInit() {
    this._pdfToolbarService.scrollableDocContainer = this.scrollableDocContainer;
    this._pdfToolbarService.docPageElements = this.pageImgListItems;
    this.initialized = true;
    if (this.canScrollToSourceBox()) {
      this.scrollToCurrentViewSourcePage();
    }
  }

  setImagePlaceholderDimensions() {
    // Make an educated guess at the dimensions of a rendered document page to determine the fixed dimensions of the
    // placeholder element. This is not exact, and is expected to be inaccurate for landscape oriented images, but is
    // not critical - the goal is make the image loading transition smoother.
    let docContainerElement = this.element.nativeElement;
    if (docContainerElement) {
      const containerRect = docContainerElement.getBoundingClientRect();
      const imageWidth = containerRect.width - 37;
      this.imgPlaceholderHeight = imageWidth * STANDARD_PAGE_HEIGHT_WIDTH_RATIO;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // ngOnInit already configures on first load; only react to subsequent input updates.
    const typeCh = changes['documentFileType'];
    const urlCh = changes['originalFileUrl'];
    const imgCh = changes['pageImgUrls'];
    const pagesCh = changes['documentPages'];

    if (typeCh && !typeCh.firstChange) {
      this.configureDocumentView();
    } else if (imgCh && !imgCh.firstChange) {
      this.configureDocumentView();
    } else if (pagesCh && !pagesCh.firstChange) {
      this.configureDocumentView();
    } else if (urlCh && !urlCh.firstChange && this.documentFileType === 'JSON') {
      this.showJson = true;
      this.pageCount = 1;
      this.loadJsonPreview();
    }

    let sourceBoxChange = false;
    if (changes.hasOwnProperty('viewSourceBox')) {
      sourceBoxChange = true;
    }
    if (changes.hasOwnProperty('viewSourcePage')) {
      sourceBoxChange = true;
    }
    if (sourceBoxChange && this.initialized && this.canScrollToSourceBox()) {
      this.scrollToCurrentViewSourcePage();
    }
    if (changes.hasOwnProperty('pageImgUrls') && !changes['pageImgUrls'].firstChange) {
      this.clearSearch(false);
    }
    this.resetCropper();
  }

  scrollToCurrentViewSourcePage() {
    const sourcePageElement = document.querySelector(`#index-${this.viewSourcePage}`);
    if (sourcePageElement) {
      sourcePageElement.scrollIntoView({behavior: 'auto', block: "center"});
    }
  }

  canScrollToSourceBox() {
    return !this.errorGettingPageImg && this.viewSourcePage !== null && !isNaN(this.viewSourcePage) &&
      this.viewSourcePage !== this._pdfToolbarService.visiblePageNumber - 1
  }

  handleScroll() {
    this._pdfToolbarService.scrollSubject$.next();
  }

  exitSplitscreen() {
    this.closeSplitscreen.emit();
  }

  resetCropper() {
    if (this.viewSourceBox !== null) {
      const pw = this.viewSourceBox.pageSize?.width;
      const ph = this.viewSourceBox.pageSize?.height;
      if (pw != null && ph != null && pw > 0 && ph > 0) {
        this.cropper = {
          x1: this.viewSourceBox.llc.x,
          y1: this.viewSourceBox.ulc.y,
          x2: this.cropper.x2 = this.viewSourceBox.lrc.x,
          y2: this.cropper.y2 = this.viewSourceBox.llc.y,
          width: pw,
          height: ph,
        };
      } else {
        this.cropper = DEFAULT_IMAGE_CROPPER;
      }
    } else {
      this.cropper = DEFAULT_IMAGE_CROPPER;
    }
  }

  getHighlightsForPage(pageIndex: number): DocumentTextHighlight[] {
    return this.highlightsPerPage.get(pageIndex + 1) || EMPTY_HIGHLIGHTS;
  }

  private buildHighlightPageCache() {
    this.highlightsPerPage = new Map();
    for (const h of this.searchHighlights) {
      if (!this.highlightsPerPage.has(h.pageNumber)) {
        this.highlightsPerPage.set(h.pageNumber, []);
      }
      this.highlightsPerPage.get(h.pageNumber).push(h);
    }
  }

  private updateActiveHighlightPageIndex() {
    if (this.activeHighlightIndex >= 0 && this.activeHighlightIndex < this.searchHighlights.length) {
      this.activeHighlightPageIndex = this.searchHighlights[this.activeHighlightIndex].pageNumber - 1;
    } else {
      this.activeHighlightPageIndex = -1;
    }
  }

  onSearchTextChange(value: string) {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.searchText = value;
    this.resetSearchState(true);
    this._cdr.detectChanges();
  }

  onSearchSubmit() {
    if (this.searchLoading) {
      return;
    }

    const trimmed = (this.searchText || '').trim();
    if (!trimmed) {
      this.clearSearch(true);
      return;
    }

    this.resetSearchState(true);
    this._cdr.detectChanges();

    const documentFileIdNumber = Number(this.documentFileId);
    if (!documentFileIdNumber || isNaN(documentFileIdNumber)) {
      this.searchError = 'Document is not available for search.';
      console.warn('Search skipped: invalid documentFileId', this.documentFileId);
      return;
    }

    this.searchError = '';
    this.searchLoading = true;
    this.searchSub?.unsubscribe();
    this._cdr.detectChanges();

    this.searchSub = this._documentFileService.searchPageText(documentFileIdNumber, trimmed)
      .pipe(finalize(() => {
        this.searchLoading = false;
        this._cdr.detectChanges();
      }))
      .subscribe({
        next: (resp) => this.handleSearchResponse(resp, trimmed),
        error: (err) => {
          this.searchError = 'Unable to search the document. Please try again.';
          this.clearSearch(true);
        }
      });
  }

  handleSearchResponse(resp: any, searchText: string) {
    const responseObjects = resp?.response?.objects || [];
    if (!responseObjects.length) {
      this.applySearchHighlights([]);
      this.searchError = '';
      return;
    }

    const matches: SearchMatch[] = [];
    responseObjects.forEach(obj => {
      if (obj?.matches?.length) {
        matches.push(...obj.matches);
      }
    });

    const highlights = this.buildHighlightsFromMatches(matches);
    this.applySearchHighlights(highlights);

    this.searchError = '';
  }

  applySearchHighlights(highlights: DocumentTextHighlight[]) {
    this.searchPerformed = true;
    if (!highlights.length) {
      this.searchHighlights = [];
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this.highlightsPerPage = new Map();
      this._cdr.detectChanges();
      return;
    }

    this.searchHighlights = highlights;
    this.buildHighlightPageCache();
    this.activeHighlightIndex = 0;
    this.activeHighlightId = this.searchHighlights[0].id;
    this.updateActiveHighlightPageIndex();
    this.searchError = '';
    this.scrollToActiveHighlight(true);
    this._cdr.detectChanges();
  }

  buildHighlightsFromMatches(matches: SearchMatch[]): DocumentTextHighlight[] {
    if (!matches || !matches.length) {
      return [];
    }

    return matches.map((match, idx) => ({
      id: `highlight-${idx}`,
      pageNumber: match.pageNumber,
      pageSize: match.pageSize,
      x1: match.ulc.x,
      y1: match.ulc.y,
      x2: match.lrc.x,
      y2: match.llc.y
    }));
  }

  clearSearch(keepText: boolean = false) {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.pdfSearchComponent?.resetSearchSubject();
    this.resetSearchState(keepText);
    this._cdr.detectChanges();
  }

  goToNextMatch() {
    if (!this.searchHighlights.length) {
      return;
    }
    this.setActiveHighlightIndex(this.activeHighlightIndex + 1);
    this._cdr.detectChanges();
  }

  goToPreviousMatch() {
    if (!this.searchHighlights.length) {
      return;
    }
    this.setActiveHighlightIndex(this.activeHighlightIndex - 1);
    this._cdr.detectChanges();
  }

  setActiveHighlightIndex(nextIndex: number) {
    if (!this.searchHighlights.length) {
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this._cdr.detectChanges();
      return;
    }

    const normalizedIndex = ((nextIndex % this.searchHighlights.length) + this.searchHighlights.length) % this.searchHighlights.length;
    this.activeHighlightIndex = normalizedIndex;
    this.activeHighlightId = this.searchHighlights[normalizedIndex].id;
    this.updateActiveHighlightPageIndex();
    this.scrollToActiveHighlight(false);
    this._cdr.detectChanges();
  }

  scrollToActiveHighlight(forceCenter = false) {
    if (this.activeHighlightId === null) {
      return;
    }
    const active = this.searchHighlights[this.activeHighlightIndex];
    const currentPage = this._pdfToolbarService.visiblePageNumber;
    if (active && active.pageNumber !== currentPage) {
      this._pdfToolbarService.visiblePageNumber = active.pageNumber;
      this._pdfToolbarService.scrollPageIntoView('auto');
    }
    this.scrollToHighlightElement(forceCenter, 3);
  }

  onHighlightsRendered() {
    if (this.activeHighlightId !== null) {
      this.scrollToHighlightElement(true, 3);
    }
  }

  private scrollToHighlightElement(forceCenter: boolean, retriesLeft: number) {
    const delay = retriesLeft >= 3 ? 0 : 250;
    setTimeout(() => {
      if (this.activeHighlightId === null) {
        return;
      }
      const highlightEl = this.element.nativeElement.querySelector(
        `[data-highlight-id="${this.activeHighlightId}"]`
      );
      if (highlightEl) {
        highlightEl.scrollIntoView({
          behavior: 'auto',
          block: forceCenter ? 'center' : 'nearest',
          inline: forceCenter ? 'center' : 'nearest'
        });
      } else if (retriesLeft > 0) {
        this.scrollToHighlightElement(forceCenter, retriesLeft - 1);
      }
    }, delay);
  }

  ngOnDestroy() {
    this.imgLoadSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.jsonFetchSub?.unsubscribe();
  }

}
